import { connect } from 'react-redux';
import styled from 'styled-components';
import { Flex } from 'components/base/layout-components';
import Text from 'components/base/Text';
import { getColor } from 'common/constants/_colors';
import { dispatchify } from 'common/utils/utils';
import { SPACES } from 'common/constants/sizes';
import { isTeamLicenseSelector } from 'state/selectors/license';
import ProvidersOAuthBackground from 'images/characters/providers-oauth-background.svg';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import { LOCAL_STORAGE_KEYS, PROVIDER_NAMES } from 'common/constants/constants';
import {
  LBGithubIcon,
  LBGitlabIcon,
  LBBitbucketIcon,
  LBAzureIcon
} from '@linearb/buzz';

const ProvidersOAuthWrapper = styled(Flex)`
  background: url(${ProvidersOAuthBackground}) no-repeat right 3.2rem bottom
    2.7rem;
  background-size: 27rem;
`;

const BorderedFlex = styled(Flex)`
  border: 1px solid ${getColor('botticelli')};
  border-radius: 6px;
  ${({ enabled = true }) => (enabled ? `cursor: pointer;` : ``)};
  &:hover {
    ${({ enabled = true }) =>
      enabled ? `background-color: ${getColor('zircon')}` : ``};
    ${({ enabled = true }) =>
      enabled ? `border: 1px solid ${getColor('dodgerBlue')};` : ``};
  }
`;

const SoonChipWrapper = styled(Flex)`
  background-color: ${getColor('fog')};
  padding: 0.2rem 0.4rem;
  position: absolute;
  top: -0.6rem;
  right: 1.5rem;
`;

export const ProvidersOAuthModal = ({
  onSelectCloudProvider,
  onSelectSelfHostedProvider,
  isOnboarding = false,
  isTeamLicense,
  setProvider = true
}) => {
  const cloudProviders = [
    {
      provider: PROVIDER_NAMES.github,
      icon: LBGithubIcon,
      label: 'GITHUB',
      onClick: () => onSelectCloudProvider({ provider: PROVIDER_NAMES.github })
    },
    {
      provider: PROVIDER_NAMES.gitlab,
      icon: LBGitlabIcon,
      label: 'GITLAB',
      onClick: () => onSelectCloudProvider({ provider: PROVIDER_NAMES.gitlab })
    },
    {
      provider: PROVIDER_NAMES.bitbucket,
      icon: LBBitbucketIcon,
      label: 'BITBUCKET',
      onClick: () =>
        onSelectCloudProvider({
          provider: PROVIDER_NAMES.bitbucket,
          withPAT: false
        })
    },
    {
      provider: PROVIDER_NAMES.azure_devops,
      label: 'AZURE DEVOPS',
      icon: LBAzureIcon,
      onClick: () =>
        onSelectCloudProvider({ provider: PROVIDER_NAMES.azure_devops })
    }
  ];

  const selfHostedProviders = [
    {
      provider: PROVIDER_NAMES.github,
      icon: LBGithubIcon,
      label: 'GITHUB ENTERPRISE SERVER',
      type: 'warning',
      onClick: () =>
        onSelectSelfHostedProvider({ provider: PROVIDER_NAMES.github })
    },
    {
      provider: PROVIDER_NAMES.gitlab,
      icon: LBGitlabIcon,
      label: 'GITLAB SERVER',
      type: 'warning',
      onClick: () =>
        onSelectSelfHostedProvider({ provider: PROVIDER_NAMES.gitlab })
    },
    {
      provider: PROVIDER_NAMES.bitbucket_server,
      icon: LBBitbucketIcon,
      label: 'BITBUCKET SERVER',
      type: 'primary',
      onClick: () =>
        onSelectSelfHostedProvider({
          provider: PROVIDER_NAMES.bitbucket_server
        })
    }
  ];

  const renderProviders = (providers, type) => (
    <Flex direction='row' pt='small' items='start'>
      {providers.map(
        ({ label, onClick, icon: Logo, isComingSoon, provider, idx }) => (
          <Flex
            data-cy={`${provider}_${type}`}
            data-testid={`${provider}_${type}`}
            key={label}
            position='relative'
            onClick={onClick}
            direction='column'
            items='center'
            justify='center'
            content='center'
            pr={idx + 1 !== providers.length ? 'medium' : ''}
          >
            <BorderedFlex
              height='6rem'
              width='6rem'
              direction='row'
              items='center'
              justify='center'
              content='center'
              enabled={!isComingSoon}
            >
              <Logo className='size-8' />
              {isComingSoon && (
                <SoonChipWrapper>
                  <Text size='x-small' weight='bold' letterSpacing='0.1rem'>
                    SOON
                  </Text>
                </SoonChipWrapper>
              )}
            </BorderedFlex>
            <Text
              pt='0.7rem'
              size='small'
              color='shuttleGray'
              width='6rem'
              align='center'
            >
              {label.toUpperCase()}
            </Text>
          </Flex>
        )
      )}
    </Flex>
  );

  const selectLoggedInProvider = () => {
    const logInProvider = BrowserStorageAccessor().local.get(
      LOCAL_STORAGE_KEYS.LAST_PROVIDER_NAME
    );
    const provider = cloudProviders.find(
      ({ provider }) => provider === logInProvider
    );
    provider && provider.onClick();
  };

  if (isOnboarding && isTeamLicense && setProvider) {
    selectLoggedInProvider();
  }
  return (
    <ProvidersOAuthWrapper direction='column' height='100%'>
      <Flex direction='column' pt='x-x-large' pl='large' pr='large'>
        <Text size='x-x-x-large' weight='300' color='black'>
          Connect your Git provider
        </Text>
        <Text size='x-large' pt={isTeamLicense ? '10rem' : SPACES['x-large']}>
          Select your source control provider:
        </Text>
        <Flex
          align='center'
          grow='1'
          justify='space-between'
          direction='column'
          pt='medium'
        >
          <Flex direction='column'>
            {!isTeamLicense && (
              <Text size='medium' color='shuttleGray'>
                Cloud
              </Text>
            )}
            {renderProviders(cloudProviders, 'cloud')}
          </Flex>
          {!isTeamLicense && (
            <Flex direction='column' pt='big'>
              <Text size='medium' color='shuttleGray'>
                Self hosted servers
              </Text>
              {renderProviders(selfHostedProviders, 'on-prem')}
            </Flex>
          )}
        </Flex>
      </Flex>
    </ProvidersOAuthWrapper>
  );
};

const mapStateToProps = state => {
  const { uiState } = state;
  return {
    uiState,
    isTeamLicense: isTeamLicenseSelector(state)
  };
};

const mapDispatchToProps = dispatch => {
  return {
    ...dispatchify(dispatch, {})
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProvidersOAuthModal);
